import { useCallback, useEffect, useMemo, useState } from 'react'
import useDataClient from './useDataClient'
import { workspaceAdapter, resourcesAdapter, rolesAdapter, sessionAdapter } from '../adapter'
import {
    AppContextType,
    AppDataControlType,
    CatalogRootType,
    CredentialType,
    NavItemDataProps,
    ResourceType,
    RepositoryGroup,
    UserAccessType,
    UserDataPart,
    AppDataBaseType,
    UserRolType,
    BlogItem,
    CredentialJiraType,
    CatalogGenericType
} from '../types'
import businessHelper from '../helpers/business.helper'
import { catalogAdapter } from '../adapter/catalogs.adapter'
import { API_SUBTYPES, DEFAULT_MENU, SDK_SUBTYPES, SOAP_SUBTYPES, SWAGGER_ARTIFACT, URLAPP, URLQUERY } from '../config'
import { useSearchParams } from 'react-router-dom'
import { blogAdapter } from '../adapter/blog.adapter'
import { ticketsAdapter } from '../adapter/tickets.adapter'

/**
 * Store and update app data 
 * @param appContext 
 * @returns 
 */
export default function useAppDataControl(appContext: AppContextType | null) {
    const [searchParams] = useSearchParams();
    const [fullLoad, setFullLoad] = useState(false)
    const [appMenu, setAppMenu] = useState<NavItemDataProps[]>([])
    const [notificationsUnread, setNotificationsUnread] = useState<number>(0)
    const accessClient = useDataClient<UserAccessType>({ appContext, disableLoading: true, initialTarget: sessionAdapter.userMenu() })
    const credentialsList = useDataClient<CredentialType[]>({ appContext });
    const adminTicketsCreds = useDataClient<CredentialJiraType[]>({ appContext });
    const catalogsList = useDataClient<CatalogRootType>({ appContext });
    const catalogsGenList = useDataClient<CatalogGenericType>({ appContext });
    const sdkList = useDataClient<RepositoryGroup<ResourceType>[]>({ appContext });
    const soapList = useDataClient<ResourceType[]>({ appContext });
    const restList = useDataClient<ResourceType[]>({ appContext });
    const linkedResourcesList = useDataClient<ResourceType[]>({ appContext });
    const rolesList = useDataClient<UserRolType[]>({ appContext });
    const blogEntriesList = useDataClient<BlogItem[]>({ appContext });
    const updateNotificationsClient = useDataClient<number>({ appContext });

    const afore = useMemo<string>(() => accessClient.status.data?.afore || '', [accessClient.status.data]);
    const rol = useMemo<number>(() => accessClient.status.data?.rol || 0, [accessClient.status.data]);
    const idUser = useMemo<number>(() => accessClient.status.data?.id || 0, [accessClient.status.data]);
    const userAddittionalInfo = useMemo<UserDataPart[] | undefined>(() => accessClient.status.data?.particularData, [accessClient.status.data]);


    const soap = useMemo<ResourceType[]>(
        () => soapList.status.data || getWebServicesInLinkedResource('soap', accessClient.status.data),
        [soapList.status.data, accessClient.status.data]
    )
    const rest = useMemo<Map<string, ResourceType[]>>(() => {
        let map = new Map<string, ResourceType[]>()
        let data = restList.status.data || getWebServicesInLinkedResource('rest', accessClient.status.data)
        for (let item of data) {
            if (map.get(item.label)) {
                map.get(item.label)?.push(item)
            } else {
                map.set(item.label, [item])
            }
        }
        return map
    }, [restList.status.data, accessClient.status.data]);

    const linkedResources = useMemo<ResourceType[]>(
        () => {
            let data = linkedResourcesList.status.data || accessClient.status.data?.linkedResources || []
            let restItems = [...rest.keys()].reduce((prev: ResourceType[], currentKey) => [...prev, ...(rest.get(currentKey) || [])], [])
            return data.filter(d => !restItems.find(r => r.id === d.id) && !soap.find(r => r.id === d.id))
        },
        [rest, soap, linkedResourcesList.status.data]
    )

    const adminTickets = useMemo<CredentialJiraType[]>(
        () => adminTicketsCreds.status.data || accessClient.status.data?.credentialsJira || [],
        [adminTicketsCreds.status.data, accessClient.status.data]
    );

    const menu = useMemo<NavItemDataProps[]>(
        () => businessHelper.generateUserMenu(appMenu, rest, soap, linkedResources, adminTickets),
        [appMenu, rest, soap, linkedResources, adminTickets, adminTickets]
    );

    const paths = useMemo<string[]>(() => [
        ...businessHelper.getUrlPaths(menu),
        URLAPP.root,
        URLAPP.start,
        URLAPP.blog,
        URLAPP.profile,
        URLAPP.apidocRest,
        URLAPP.apidocRestDoc,
        URLAPP.apidocSoap,
        URLAPP.apidocSoapDoc,
        URLAPP.apidocGroup,
        URLAPP.linkedResources,
        URLAPP.linkedResourcesGroup,
        '/'
    ], [menu])

    const credentials = useMemo<CredentialType[]>(
        () => credentialsList.status.data || accessClient.status.data?.credentials || [],
        [credentialsList.status.data, accessClient.status.data]
    );

    const catalogApp = useMemo<CatalogRootType | undefined>(
        () => catalogsList.status.data || accessClient.status.data?.catalogApp,
        [catalogsList.status.data, accessClient.status.data]
    )

    const catalogGeneric = useMemo<CatalogGenericType | undefined>(
        () => catalogsGenList.status.data || accessClient.status.data?.genericCatalog,
        [catalogsGenList.status.data, accessClient.status.data]
    )

    const sdk = useMemo<RepositoryGroup<ResourceType>[]>(
        () => sdkList.status.data || accessClient.status.data?.sdk || [],
        [sdkList.status.data, accessClient.status.data]
    )

    const roles = useMemo<UserRolType[]>(
        () => rolesList.status.data || accessClient.status.data?.roles || [],
        [rolesList.status.data, accessClient.status.data]
    )

    const blogEntries = useMemo<BlogItem[]>(() => blogEntriesList.status.data || [], [blogEntriesList.status.data])

    const dataBase: AppDataBaseType = {
        afore,
        rol,
        idUser,
        notificationsUnread,
        user: (appContext?.user.data?.user || ''),
        userInfo: userAddittionalInfo || [],
        menu,
        paths,
        rest,
        catalogApp,
        credentials,
        adminTickets,
        catalogGeneric,
        sdk,
        soap,
        linkedResources,
        blogEntries,
        roles
    }

    const fullReload = useCallback(() => {
        accessClient.executeRequest()
    }, [])

    const update = useCallback((requestType: AppDataControlType) => {
        switch (requestType) {
            case 'catalogApp':
                catalogsList.executeRequest(catalogAdapter.getData())
                break;
            case 'catalogGeneric':
                catalogsGenList.executeRequest(catalogAdapter.listAllGroupData())
                break;
            case 'credentials':
                credentialsList.executeRequest(workspaceAdapter.getAll())
                break;
            case 'adminTickets':
                adminTicketsCreds.executeRequest(ticketsAdapter.getJiraCredentials())
                break;
            case 'sdk':
                sdkList.executeRequest(resourcesAdapter.getResourcesBySubtype(SDK_SUBTYPES, afore))
                break;
            case 'rest':
                restList.executeRequest(resourcesAdapter.getResourceListBySubtype(API_SUBTYPES, afore))
                break;
            case 'soap':
                soapList.executeRequest(resourcesAdapter.getResourceListBySubtype(SOAP_SUBTYPES, afore))
                break;
            case 'linkedResources':
                linkedResourcesList.executeRequest(resourcesAdapter.getResourceListByRol(0, afore))
                break;
            case 'roles':
                rolesList.executeRequest(rolesAdapter.getRoles())
                break;
            case 'blogEntries':
                blogEntriesList.executeRequest(blogAdapter.getEntries())
                break;
            default:
                break;
        }
    }, [afore]);

    useEffect(() => {
        if (accessClient.status.data) {
            setAppMenu(accessClient.status.data.menu);
            setNotificationsUnread(accessClient.status.data.notificationsUnread);
            setFullLoad(true);
        }
        if (accessClient.status.error) {
            setAppMenu(DEFAULT_MENU)
        }
    }, [accessClient.status])

    useEffect(() => {
        let notificationReadId = searchParams.get(URLQUERY.readId)
        if (notificationReadId) {
            updateNotificationsClient.executeRequest(sessionAdapter.updateNotification(notificationReadId))
        }
    }, [searchParams])

    useEffect(() => {
        if (updateNotificationsClient.status.data !== null && updateNotificationsClient.status.data !== undefined) {
            setNotificationsUnread(updateNotificationsClient.status.data)
        }
    }, [updateNotificationsClient.status])

    return {
        offline: !!accessClient.status.error,
        fullLoad: fullLoad,
        dataBase,
        update,
        fullReload
    }
}

function getWebServicesInLinkedResource(wsType: 'rest' | 'soap', dataset?: UserAccessType | null): ResourceType[] {
    let webServicesMenu = dataset?.menu.find(m => m.path === URLAPP.apidoc)?.id || 0
    let filteredService = dataset?.[wsType] || dataset?.linkedResources?.filter(m => {
        let isRest = !!m.versions[0].artifacts.find(a => a.type === SWAGGER_ARTIFACT)
        return m.linkedMenus?.includes(webServicesMenu) && wsType === (isRest ? 'rest' : 'soap')
    }) || []
    return filteredService.sort((a, b) => a.name > b.name ? 1 : -1)
}